/**
 * Rutas de billing — facturación y compra: facturas, presupuestos, gastos, clientes y proveedores.
 *
 * Se componen en `src/pages/index.jsx`. Es un fragmento, no un array: React Router
 * recorre los `<React.Fragment>` hijos de `<Routes>`, así que el JSX se mudó tal cual
 * —sin `key` ni comas— y el diff es el movimiento, nada más.
 *
 * `routeTable.test.jsx` congela las 174 rutas de la app contra la tabla previa a la
 * mudanza: perder una ruta aquí no da error, da un 404.
 */
import { Route, Navigate } from 'react-router-dom';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { PermissionGuard } from '@/components/auth/PermissionGuard';
import { SupplierReadGuard } from '@/components/auth/SupplierReadGuard';
import { PERMS } from '@/constants/permissions';

const FacturasHub = lazyWithRetry(() => import("@/pages/FacturasHub"));
const FacturaDetalle = lazyWithRetry(() => import("@/pages/FacturaDetalle"));
const FacturaForm = lazyWithRetry(() => import("@/pages/FacturaForm"));
const QuoteFormV2 = lazyWithRetry(() => import("@/pages/QuoteFormV2"));
const PlantillasFactura = lazyWithRetry(() => import("@/pages/PlantillasFactura"));
const Gastos = lazyWithRetry(() => import("@/pages/Gastos"));
const ClienteDetalle = lazyWithRetry(() => import("@/pages/ClienteDetalle"));
const ProveedorDetalle = lazyWithRetry(() => import("@/pages/ProveedorDetalle"));

export const routes = (
  <>
                <Route path="/facturacion" element={<PermissionGuard permission={PERMS.INVOICING_VIEW} fallback={<Navigate to="/" replace />}><Navigate to="/facturas" replace /></PermissionGuard>} />

                {/* PR4-bis #1 — /facturas is the Hub (Lista + TicketBAI +
                    VeriFactu como tabs). The legacy /veri-factu and
                    /ticket-bai-status redirect to ?tab= for bookmarks. */}
                <Route path="/facturas" element={<PermissionGuard permission={PERMS.INVOICING_VIEW} fallback={<Navigate to="/" replace />}><FacturasHub /></PermissionGuard>} />

                <Route path="/factura-detalle" element={<PermissionGuard permission={PERMS.INVOICING_VIEW} fallback={<Navigate to="/" replace />}><FacturaDetalle /></PermissionGuard>} />

                <Route path="/factura-form" element={<PermissionGuard permission={PERMS.INVOICING_VIEW} fallback={<Navigate to="/" replace />}><FacturaForm /></PermissionGuard>} />

                {/* El guard faltaba: era la ÚNICA ruta del manifest sin él, así que
                    un usuario sin permiso de facturación podía abrir el formulario de
                    presupuesto tecleando la URL. Los permisos de verdad los aplica el
                    backend, pero la UI no debe ofrecer lo que no se puede usar. */}
                <Route path="/quote-form" element={<PermissionGuard permission={PERMS.INVOICING_VIEW} fallback={<Navigate to="/" replace />}><QuoteFormV2 /></PermissionGuard>} />

                <Route path="/plantillas-factura" element={<PermissionGuard permission={PERMS.INVOICING_VIEW} fallback={<Navigate to="/" replace />}><PlantillasFactura /></PermissionGuard>} />

                <Route path="/gastos" element={<PermissionGuard permission={PERMS.ACCOUNTING_VIEW} fallback={<Navigate to="/" replace />}><Gastos /></PermissionGuard>} />

                <Route path="/gastos-escaneos" element={<Navigate to="/Gastos?tab=ocr" replace />} />

                {/* Listado retirado — el directorio unificado /contactos lo reemplaza.
                    La ficha /clientes/:id (Customer 360) se conserva intacta abajo. */}
                <Route path="/clientes" element={<Navigate to="/contactos" replace />} />

                {/* Customer 360 — ficha unificada de cliente (path param como /payrolls/:id) */}
                <Route path="/clientes/:id" element={<PermissionGuard permission={PERMS.CRM_VIEW} fallback={<Navigate to="/" replace />}><ClienteDetalle /></PermissionGuard>} />


                {/* Fusion legacy redirects (ADR-0005) — rutas PascalCase de las páginas
                    pre-fusión que ahora son tabs de su canónica. Aditivo: no colisiona con
                    las hyphen-lowercase existentes (difieren en el guion). Cada destino lleva
                    el ?tab= correspondiente y la canónica ya rinde los tabs. equipo-fusion se
                    excluye aposta: su /Organigrama colisiona con /organigrama (mismo path
                    case-insensitive) y requiere deprecar el standalone — decisión aparte. */}
                {/* Customers */}
                <Route path="/CRMContactos" element={<Navigate to="/contactos" replace />} />

                {/* Expenses */}
                <Route path="/GastosEscaneos" element={<Navigate to="/gastos?tab=ocr" replace />} />

                {/* Listado retirado — el directorio unificado /contactos lo reemplaza.
                    La ficha /proveedores/:id (Supplier 360) se conserva intacta abajo. */}
                <Route path="/proveedores" element={<Navigate to="/contactos" replace />} />

                {/* Supplier 360 — ficha unificada de proveedor (guard de lectura de proveedor) */}
                <Route path="/proveedores/:id" element={<SupplierReadGuard fallback={<Navigate to="/" replace />}><ProveedorDetalle /></SupplierReadGuard>} />
  </>
);
